import React, { useState, useEffect } from 'react';
import { api, getStation } from '../api';

const DAYS  = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
const HOURS = ['12a','2a','4a','6a','8a','10a','12p','2p','4p','6p','8p','10p'];

function StatCard({ label, value, sub }) {
  return (
    <div style={{ background:'#fff', borderRadius:12, padding:18, border:'1px solid #f1f5f9' }}>
      <div style={{ fontSize:11, fontWeight:600, color:'#94a3b8', textTransform:'uppercase', letterSpacing:0.5, marginBottom:8 }}>{label}</div>
      <div style={{ fontSize:26, fontWeight:800, color:'#0f172a', lineHeight:1 }}>{value}</div>
      {sub && <div style={{ fontSize:12, color:'#94a3b8', marginTop:4 }}>{sub}</div>}
    </div>
  );
}

export default function Analytics() {
  const station = getStation();
  const [range, setRange] = useState('7d');
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!station) { setLoading(false); return; }
    setLoading(true);
    api.get(`/broadcast/analytics/${station.id}?range=${range}`)
      .then(d => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [station, range]);

  if (!station) return (
    <div style={{ textAlign:'center', padding:'60px 20px', background:'#fff', borderRadius:16, border:'2px dashed #e2e8f0' }}>
      <div style={{ fontSize:48, marginBottom:12 }}>📊</div>
      <h3 style={{ margin:'0 0 8px', color:'#0f172a' }}>No station selected</h3>
      <p style={{ color:'#64748b', fontSize:14 }}>Select a station in <strong>My Stations</strong> to view analytics.</p>
    </div>
  );

  // Listener metrics come from live streaming (AzuraCast) data. Until that is connected the
  // backend returns data_available:false and we show an honest empty state instead of numbers.
  const listenerDataAvailable = !!(data && data.data_available);

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:20 }}>
        <div style={{ display:'flex', gap:4, background:'#f8fafc', padding:4, borderRadius:8 }}>
          {['24h','7d','30d'].map(r => (
            <button key={r} onClick={() => setRange(r)} style={{ padding:'6px 14px', borderRadius:6, border:'none', cursor:'pointer', fontSize:12, fontWeight:600, background: range===r ? '#6366f1' : 'transparent', color: range===r ? '#fff' : '#64748b' }}>{r}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:14 }}>
          {[1,2,3,4,5].map(i => <div key={i} style={{ height:90, background:'#f1f5f9', borderRadius:12 }} />)}
        </div>
      ) : (
        <>
          {/* AI clips generated is real (counted from the clips table) and always shown. */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:14, marginBottom:20 }}>
            <StatCard label="AI Clips Generated" value={data?.total_clips_generated?.toLocaleString() ?? 0} sub={`last ${range}`} />
          </div>

          {listenerDataAvailable ? (
            <>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:14, marginBottom:20 }}>
                <StatCard label="Listener Hours"  value={data?.total_listener_hours?.toLocaleString() ?? 0} sub={`last ${range}`} />
                <StatCard label="Peak Concurrent" value={data?.peak_concurrent ?? 0}                        sub="listeners"       />
                <StatCard label="Avg Session"     value={`${data?.avg_session_minutes ?? 0}m`}              sub="per listener"    />
                <StatCard label="AI Host Uptime"  value={`${data?.ai_host_uptime_pct ?? 0}%`}               sub="last 30 days"    />
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 }}>
                {/* Hourly */}
                <div style={{ background:'#fff', borderRadius:12, padding:20, border:'1px solid #f1f5f9' }}>
                  <h4 style={{ margin:'0 0 14px', fontSize:13, fontWeight:700, color:'#0f172a' }}>Hourly Listener Pattern</h4>
                  <div style={{ display:'flex', height:80, alignItems:'flex-end', gap:2 }}>
                    {(data?.hourly||[]).map((h,i) => {
                      const max = Math.max(...(data?.hourly||[]).map(x=>x.listeners));
                      const pct = max > 0 ? (h.listeners/max)*100 : 4;
                      return <div key={i} title={`${h.hour}: ${h.listeners}`} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center' }}><div style={{ width:'70%', height:`${Math.max(pct,4)}%`, background:'#6366f1', borderRadius:'2px 2px 0 0', opacity:0.8 }} /></div>;
                    })}
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', marginTop:5 }}>
                    {HOURS.map(h => <span key={h} style={{ fontSize:9, color:'#94a3b8' }}>{h}</span>)}
                  </div>
                </div>

                {/* Daily */}
                <div style={{ background:'#fff', borderRadius:12, padding:20, border:'1px solid #f1f5f9' }}>
                  <h4 style={{ margin:'0 0 14px', fontSize:13, fontWeight:700, color:'#0f172a' }}>Daily Listeners vs AI Clips</h4>
                  <div style={{ display:'flex', height:80, alignItems:'flex-end', gap:4 }}>
                    {(data?.daily||[]).map((d,i) => {
                      const maxL = Math.max(...(data?.daily||[]).map(x=>x.listeners));
                      const maxC = Math.max(...(data?.daily||[]).map(x=>x.clips));
                      return (
                        <div key={i} style={{ flex:1, display:'flex', alignItems:'flex-end', gap:1 }}>
                          <div style={{ flex:1, height:`${maxL>0?(d.listeners/maxL)*100:4}%`, background:'#6366f1', borderRadius:'2px 2px 0 0' }} />
                          <div style={{ flex:1, height:`${maxC>0?(d.clips/maxC)*80:4}%`,      background:'#10b981', borderRadius:'2px 2px 0 0' }} />
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', marginTop:5 }}>
                    {DAYS.map(d => <span key={d} style={{ fontSize:9, color:'#94a3b8' }}>{d}</span>)}
                  </div>
                  <div style={{ display:'flex', gap:14, marginTop:8 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:5 }}><div style={{ width:10,height:10,borderRadius:2,background:'#6366f1' }}/><span style={{ fontSize:11,color:'#64748b' }}>Listeners</span></div>
                    <div style={{ display:'flex', alignItems:'center', gap:5 }}><div style={{ width:10,height:10,borderRadius:2,background:'#10b981' }}/><span style={{ fontSize:11,color:'#64748b' }}>AI Clips</span></div>
                  </div>
                </div>
              </div>

              {/* Content breakdown */}
              <div style={{ background:'#fff', borderRadius:12, padding:20, border:'1px solid #f1f5f9' }}>
                <h4 style={{ margin:'0 0 14px', fontSize:13, fontWeight:700, color:'#0f172a' }}>AI Content Type Breakdown</h4>
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {(data?.top_content||[]).map((c,i) => (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:12 }}>
                      <div style={{ width:110, fontSize:12, color:'#374151', fontWeight:500 }}>{c.type}</div>
                      <div style={{ flex:1, height:8, background:'#f1f5f9', borderRadius:4, overflow:'hidden' }}>
                        <div style={{ height:'100%', width:`${c.pct}%`, background:['#6366f1','#0ea5e9','#10b981','#f59e0b'][i]||'#6366f1', borderRadius:4 }} />
                      </div>
                      <div style={{ width:60, fontSize:12, color:'#64748b', textAlign:'right' }}>{c.count} · {c.pct}%</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div style={{ textAlign:'center', padding:'48px 20px', background:'#fff', borderRadius:16, border:'2px dashed #e2e8f0' }}>
              <div style={{ fontSize:40, marginBottom:12 }}>📡</div>
              <h3 style={{ margin:'0 0 8px', color:'#0f172a', fontSize:16 }}>Listener analytics coming soon</h3>
              <p style={{ color:'#64748b', fontSize:14, maxWidth:420, margin:'0 auto' }}>Listener analytics will appear once your station connects streaming data.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
